import { Component, OnInit, ViewChild, Input, ContentChild, TemplateRef, EventEmitter, AfterViewInit, Output } from '@angular/core';
import { NotificationComponent } from 'shared/components/notification/notification.component';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ResponsiveDatatableComponent } from '../responsive-datatable/responsive-datatable.component';
import { TableColumn, SortEvent, TableLoadEvent } from '../responsive-datatable/table.model';
import { Observable } from 'rxjs';
import { L10nTranslationService } from 'angular-l10n';
import { SecurityService } from 'core/security.service';
import { FilterService } from 'core/filter.service';
import { System } from 'shared/service-clients/system';
import { Item, ClientService } from 'core/client.service';

export interface IListOptions {
  rightButtonShow?: boolean;
  rightButton?: string;
  rightButtonIcon?: string;
  rightButtonColor?: string;
  rightButtonClass?: string;
  alwaysEnabled?: boolean;
  rightButtonDisabled?: boolean;
  rightButtonEvent?: EventEmitter<any>;
  
  showInfoIcon?: boolean;
  helperText?: string;
}

@Component({
  selector: 'samplicity-list-page',
  templateUrl: './list-page.component.html',
  styleUrls: ['./list-page.component.scss']
})
export class ListPageComponent implements OnInit, AfterViewInit {
  private clientIdChangeKey = 'samplicity-dtp-changed-client';

  @ViewChild('table') table: ResponsiveDatatableComponent;
  @ViewChild('notificationWindow', { static: true }) notificationWindow: NotificationComponent;
  @ViewChild('blackoutHoldNotificationWindow', { static: true }) blackoutHoldNotificationWindow: NotificationComponent;
  @ContentChild(TemplateRef) cellTemplate: TemplateRef<any>;

  @Input() filterForm: FormGroup;
  @Input() columns: TableColumn[];
  @Input() items: any[];
  @Input() loadMore: (loadEvent: TableLoadEvent) => Observable<any>;
  @Input() delayedLoadMore: boolean;
  @Input() createLink: string;
  @Input() createBtnText: string;
  @Input() createLinkPermission: string;
  @Input() pageTitle: string;
  @Input() stickyResetItem: string;
  @Input() breadcrumbText: string;
  @Input() options: IListOptions[];
  @Input() displayColumns: TableColumn[];
  @Input() hightlightProp = 'highlight';
  @Input() createLinkVisible = true;
  @Input() hideCreateButton = false;
  @Input() defaultFilterValues: Object;
  @Input() selectEnabled = false;
  @Input() selectColumn: TableColumn;
  @Input() disableSelectSorting: boolean;
  @Input() itemSelectableProp;
  @Input() helperText: string | undefined = undefined
  @Input() perPage: number = 50;
  @Output() selectEvent: EventEmitter<object[]> = new EventEmitter<object[]>();

  searchForm: FormGroup;
  tableCountDisplay: string;
  scrollToTopRequired: boolean = false;

  constructor(
    private translationService: L10nTranslationService,
    private securityService: SecurityService,
    private filterService: FilterService,
    private route: ActivatedRoute,
    private systemService: System,
    public clientService: ClientService,
    fb: FormBuilder) {

    this.searchForm = fb.group({
      search: ['']
    });
  }

  ngOnInit() {
    this.initFilter();
    this.loadBlackoutHoldBanner();

    this.route.queryParams.subscribe(params => {
      if (params.discarded) {
        this.notificationWindow.displayInfoMessage(
          this.translationService.translate('Notifications.ChangesDiscardedSuccess').replace('$0', params.discarded),
          'Notifications.InfoTitleDefault'
        );
        this.scrollToTopRequired = true;
      }
      if (params.deleted) {
        this.notificationWindow.displaySuccessMessage(
          this.translationService.translate('Notifications.DeletedSuccess').replace('$0', params.deleted),
          'Notifications.SuccessTitleDefault'
        );
        this.scrollToTopRequired = true;
      }
      if (params.canceled) {
        this.notificationWindow.displaySuccessMessage(
          this.translationService.translate('Notifications.CanceledSuccess').replace('$0', params.canceled),
          'Notifications.SuccessTitleDefault'
        );
        this.scrollToTopRequired = true;
      }
      this.setClientChange();
    });

    const hasColumnPermissions = this.columns.some(x => !!(x.linkPermission));
    if (this.createLinkPermission || hasColumnPermissions) {
      this.securityService.getPermissions().then(permissions => {
        if (this.createLinkPermission) {
          const createLinkPermission = permissions.some(x => x.toLowerCase() === this.createLinkPermission.toLowerCase());
          if (createLinkPermission) {
            this.createLinkVisible = true;
          } else {
            this.createLinkVisible = false;
          }
        }
        if (hasColumnPermissions) {
          for (const col of this.columns) {
            if (col.linkPermission) {
              const colPermissions = permissions.some(x => x.toLowerCase() === col.linkPermission.toLowerCase());
              if (!colPermissions) {
                col.disableLink = true;
              }
            }
          }
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.scrollToTopRequired) {
      this.scrollToTop();
    }
  }

  scrollToTop(): void {
    const sidenav = document.querySelector('.mat-sidenav-content');
    sidenav ? sidenav.scrollTo(0, 0) : window.scrollTo(0, 0);
    this.scrollToTopRequired = false;
  }

  initFilter(): void {
    if (this.filterService.hasFilter(this.pageTitle)) {
      const filterValue = this.filterService.loadFilter(this.pageTitle);
      if (Object.keys(filterValue).length > 0) {
        this.filterForm.patchValue(filterValue);
      } else {
        this.filterForm.reset();
      }
    } else {
      this.filterForm.reset();
    }
  }

  getFilterValue(name: string): any {
    const field = this.filterForm.get(name);
    if (field) {
      return field.value;
    }
    return null;
  }

  hasFilter(): boolean {
    return this.filterService.hasFilter(this.pageTitle);
  }

  getSearchValue(): string {
    const field = this.searchForm.get('search');
    if (field) {
      return field.value;
    }
    return '';
  }

  doSearch() {
    if (this.displayColumns) {
      this.columns = this.displayColumns;
    }
    if (this.filterForm.valid && this.searchForm.valid) {
      this.filterService.saveFilter(this.pageTitle, this.filterForm.getRawValue());
      this.notificationWindow.removeMessage();
      this.table.triggerLoadData();
      this.options && this.options.filter(x => !x.alwaysEnabled).forEach(x => x.rightButtonDisabled = true);
    }
  }

  getSelectedItems() {
    return this.table.getSelectedItems();
  }

  onClearFilter(stickyItem?: string) {
    let stickyValue: any;
    this.filterService.clearFilter(this.pageTitle);
    if (stickyItem != null) {
      stickyValue = this.filterForm.get(stickyItem).value;
    }
    this.filterForm.reset();
    this.options && this.options.filter(x => !x.alwaysEnabled).forEach(x => x.rightButtonDisabled = true);
    if (this.defaultFilterValues) {
      Object.keys(this.defaultFilterValues).forEach((key) => {
        this.filterForm.get(key).setValue(this.defaultFilterValues[key]);
      });
    }
    if (stickyValue != null) {
      this.filterForm.get(stickyItem).setValue(stickyValue);
    }
    this.table.triggerLoadData();
  }

  onToggleFilter(toggle: boolean) {
    this.filterService.toggleFilter(toggle);
  }

  setClientChange(): void {
    localStorage.setItem(this.clientIdChangeKey, JSON.stringify(false));
  }

  onSelect(selectedItems): void {
    this.selectEvent.emit(selectedItems);
  }

  loadBlackoutHoldBanner(): void {
    this.systemService.getBlackoutHoldCount().subscribe(
      x => {
        if (x.result > 0) {
          const zipCodeMessage = this.translationService.translate('Notifications.BlackoutHold.BlackoutHoldBannerText1')
            .replace('{0}', x.result);
          const selectedClientId = this.clientService.getClientId();

          const messageList = [
            new Item('blackoutHold', zipCodeMessage),
            new Item(null, this.translationService.translate('Notifications.BlackoutHold.BlackoutHoldBannerText2')),
            new Item(['/' + selectedClientId + '/orders/', 'blackoutHolds'],
              this.translationService.translate('Notifications.BlackoutHold.BlackoutHoldBannerText3'))
          ];
          this.blackoutHoldNotificationWindow
            .displayInfoMessageWithLinks(messageList, 'Notifications.BlackoutHold.BlackoutHoldBannerTitle');
        } else {
          this.blackoutHoldNotificationWindow.removeAllMessages();
        }
      }
    );
  }
}
